import React, { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Finance = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [walletData, setWalletData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [transactionsData, setTransactionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Date Filters
  const [earningsStartDate, setEarningsStartDate] = useState(() => {
    const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return start.toISOString().split("T")[0];
  });
  const [earningsEndDate, setEarningsEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Service Filters
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedSubServiceId, setSelectedSubServiceId] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // "all" | "ledger" | "daily"

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  // Quick Date Presets
  const applyDatePreset = (preset) => {
    const now = new Date();
    let start;
    if (preset === "7d") {
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (preset === "30d") {
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (preset === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    setEarningsStartDate(start.toISOString().split("T")[0]);
    setEarningsEndDate(now.toISOString().split("T")[0]);
    setCurrentPage(1);
  };

  const fetchFinanceData = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const startDate = earningsStartDate;
      const endDate = earningsEndDate;

      // 1. Fetch wallet summary (fixed endpoint path with /admin)
      const walletPromise = fetch(`${BASE_URL}admin/account/wallet`, { headers })
        .then((res) => res.json())
        .catch((err) => {
          console.error("Wallet fetch error:", err);
          return null;
        });

      // 2. Fetch daily earnings (fixed endpoint path with /admin)
      const earningsPromise = fetch(
        `${BASE_URL}admin/account/earnings/daily?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      )
        .then((res) => res.json())
        .catch((err) => {
          console.error("Earnings fetch error:", err);
          return null;
        });

      // 3. Fetch insights (fixed endpoint path with /admin)
      const insightsPromise = fetch(`${BASE_URL}admin/account/insights`, {
        headers,
      })
        .then((res) => res.json())
        .catch((err) => {
          console.error("Insights fetch error:", err);
          return null;
        });

      // 4. Fetch transactions (fixed endpoint path with /admin)
      let transUrl = `${BASE_URL}admin/account/transactions?page=${currentPage}&limit=${itemsPerPage}`;
      if (selectedServiceId) transUrl += `&serviceId=${selectedServiceId}`;
      if (selectedSubServiceId) transUrl += `&subServiceId=${selectedSubServiceId}`;
      if (startDate) transUrl += `&startDate=${startDate}`;
      if (endDate) transUrl += `&endDate=${endDate}`;

      const transPromise = fetch(transUrl, { headers })
        .then((res) => res.json())
        .catch((err) => {
          console.error("Transactions fetch error:", err);
          return null;
        });

      const [walletJson, earningsJson, insightsJson, transJson] =
        await Promise.all([
          walletPromise,
          earningsPromise,
          insightsPromise,
          transPromise,
        ]);

      if (walletJson?.status === "success") {
        setWalletData(walletJson.data);
      }
      if (earningsJson?.status === "success") {
        setEarningsData(earningsJson.data);
      }
      if (insightsJson?.status === "success") {
        setInsightsData(insightsJson.data);
      }
      if (transJson?.status === "success") {
        setTransactionsData(transJson.data);
      }
    } catch (err) {
      console.error("Error fetching finance data:", err);
    }
    setLoading(false);
    setRefreshing(false);
  }, [
    currentPage,
    earningsStartDate,
    earningsEndDate,
    selectedServiceId,
    selectedSubServiceId,
  ]);

  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  // Fetch services for category filter dropdown
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}admin/services`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json?.status === "success") {
          setServices(json.data.services || []);
        }
      } catch (err) {
        console.error("Failed to load services for filter", err);
      }
    };
    fetchServices();
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const totalPages = transactionsData?.pagination?.total
    ? Math.ceil(transactionsData.pagination.total / itemsPerPage)
    : 1;

  // Selected Service Sub-Services helper
  const availableSubServices = useMemo(() => {
    if (!selectedServiceId) return [];
    const found = services.find((s) => s.id === selectedServiceId);
    return found?.subServices || [];
  }, [services, selectedServiceId]);

  if (loading && !walletData && !earningsData) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] text-stone-900 flex flex-col font-sans">
        <Sidebar admin={admin} onLogout={handleLogout} />
        <main className="lg:ml-72 min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-stone-200 border-t-[#1C1917] rounded-full animate-spin"></div>
            <p className="text-sm font-semibold text-stone-600">
              Loading Financial Ledger & Insights...
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-stone-900 flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">
                <span>Admin</span>
                <span>/</span>
                <span>Financial Ledger</span>
                <span>/</span>
                <span className="text-stone-900">Revenue & Commissions</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-stone-900 tracking-tight flex items-center gap-3">
                <span>Finance & Remittance</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Live Audit Trail
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Monitor platform commission cuts, buddy provider shares, gross GMV, and wallet settlements.
              </p>
            </div>

            {/* Quick Action / Refresh */}
            <div className="flex items-center gap-2 self-start xl:self-auto">
              <button
                onClick={() => fetchFinanceData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs disabled:opacity-60"
              >
                <svg
                  className={`w-4 h-4 text-[#F8D346] ${refreshing ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>{refreshing ? "Syncing..." : "Refresh Ledger"}</span>
              </button>
            </div>
          </div>
        </header>

        <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-6">
          {/* Filter & Range Bar */}
          <div className="bg-white rounded-2xl border border-stone-200 p-4 lg:p-5 shadow-xs flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
            {/* Date Range & Presets */}
            <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-500 mr-1">
                Timeframe:
              </span>
              <button
                onClick={() => applyDatePreset("7d")}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => applyDatePreset("30d")}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => applyDatePreset("month")}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors"
              >
                This Month
              </button>

              <div className="flex items-center gap-1.5 ml-0 sm:ml-2">
                <input
                  type="date"
                  value={earningsStartDate}
                  onChange={(e) => {
                    setEarningsStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-stone-50 border border-stone-300 text-stone-800 text-xs font-medium rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                />
                <span className="text-stone-400 text-xs font-bold">—</span>
                <input
                  type="date"
                  value={earningsEndDate}
                  onChange={(e) => {
                    setEarningsEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-stone-50 border border-stone-300 text-stone-800 text-xs font-medium rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                />
              </div>
            </div>

            {/* Service & Sub-Service Filter */}
            <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-500 mr-1">
                Category:
              </span>
              <select
                value={selectedServiceId}
                onChange={(e) => {
                  setSelectedServiceId(e.target.value);
                  setSelectedSubServiceId("");
                  setCurrentPage(1);
                }}
                className="bg-stone-50 border border-stone-300 text-stone-800 text-xs font-medium rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
              >
                <option value="">All Services</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>

              {selectedServiceId && availableSubServices.length > 0 && (
                <select
                  value={selectedSubServiceId}
                  onChange={(e) => {
                    setSelectedSubServiceId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-stone-50 border border-stone-300 text-stone-800 text-xs font-medium rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                >
                  <option value="">All Sub-Services</option>
                  {availableSubServices.map((ss) => (
                    <option key={ss.id} value={ss.id}>
                      {ss.name}
                    </option>
                  ))}
                </select>
              )}

              {(selectedServiceId || selectedSubServiceId) && (
                <button
                  onClick={() => {
                    setSelectedServiceId("");
                    setSelectedSubServiceId("");
                    setCurrentPage(1);
                  }}
                  className="text-stone-400 hover:text-stone-700 text-xs font-bold px-2 py-1"
                  title="Clear Service Filter"
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>

          {/* Section 1: Top Platform Wallet & Balance KPI Cards */}
          {walletData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Wallet Balance */}
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Platform Wallet
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                    ₹
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-stone-900 tracking-tight">
                    ₹{(walletData.balance || 0).toLocaleString()}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold mt-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    <span>Liquid Net Reserve</span>
                  </div>
                </div>
              </div>

              {/* Lifetime Commission */}
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Lifetime Commission
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-[#F8D346]/20 text-[#1C1917] flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-stone-900 tracking-tight">
                    ₹{(walletData.lifetimeCommission || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-medium">
                    {walletData.lifetimeCommissionTransactions || 0} commissions collected
                  </div>
                </div>
              </div>

              {/* Last 7 Days Commission */}
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Last 7 Days Cut
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-stone-900 tracking-tight">
                    ₹{(walletData.last7DaysCommission || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-medium">
                    {walletData.last7DaysCommissionTransactions || 0} recent transactions
                  </div>
                </div>
              </div>

              {/* Avg Fee Per Order */}
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs flex flex-col justify-between">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                    Avg Fee / Order
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-stone-900 tracking-tight">
                    ₹
                    {walletData.lifetimeCommissionTransactions > 0
                      ? Math.round(
                          walletData.lifetimeCommission /
                            walletData.lifetimeCommissionTransactions
                        ).toLocaleString()
                      : 0}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-medium">
                    Platform take per completed job
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Gross GMV vs Commission vs Provider Split (Selected Date Range) */}
          {earningsData?.totals && (
            <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
                <div>
                  <h3 className="text-lg font-bold text-stone-900">
                    Gross GMV & Remittance Breakdown
                  </h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    For selected range: {earningsStartDate} to {earningsEndDate}
                  </p>
                </div>
                <div className="text-xs font-semibold px-3 py-1 bg-stone-100 rounded-full text-stone-700 self-start sm:self-auto">
                  {earningsData.totals.count || 0} Total Completed Orders
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Gross Booking Volume */}
                <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                    Gross Invoiced (GMV)
                  </span>
                  <span className="text-2xl font-extrabold text-stone-900 block mt-1">
                    ₹{(earningsData.totals.gross || 0).toLocaleString()}
                  </span>
                  <span className="text-xs text-stone-400 mt-0.5 block">
                    Customer billings before splits
                  </span>
                </div>

                {/* Platform Commission Cut */}
                <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-200">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900 block">
                    Platform Commission (Net)
                  </span>
                  <span className="text-2xl font-extrabold text-amber-950 block mt-1">
                    ₹{(earningsData.totals.adminCommission || 0).toLocaleString()}
                  </span>
                  <span className="text-xs text-amber-800/80 mt-0.5 block">
                    {earningsData.totals.gross > 0
                      ? `${(
                          (earningsData.totals.adminCommission /
                            earningsData.totals.gross) *
                          100
                        ).toFixed(1)}% effective take-rate`
                      : "0% take-rate"}
                  </span>
                </div>

                {/* Provider Net Earnings */}
                <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-200">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-900 block">
                    Buddy / Provider Payouts
                  </span>
                  <span className="text-2xl font-extrabold text-emerald-950 block mt-1">
                    ₹{(earningsData.totals.providerEarnings || 0).toLocaleString()}
                  </span>
                  <span className="text-xs text-emerald-800/80 mt-0.5 block">
                    {earningsData.totals.gross > 0
                      ? `${(
                          (earningsData.totals.providerEarnings /
                            earningsData.totals.gross) *
                          100
                        ).toFixed(1)}% paid to buddies`
                      : "0% paid to buddies"}
                  </span>
                </div>

                {/* Orders Count */}
                <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                    Completed Jobs
                  </span>
                  <span className="text-2xl font-extrabold text-stone-900 block mt-1">
                    {earningsData.totals.count || 0}
                  </span>
                  <span className="text-xs text-stone-400 mt-0.5 block">
                    {earningsData.totals.count > 0
                      ? `₹${Math.round(
                          earningsData.totals.gross / earningsData.totals.count
                        )} avg order value`
                      : "No completed jobs"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Performance Insights & Top Trades */}
          {insightsData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Period Comparison Card */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-xs flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">
                    Comparative Period Velocity
                  </h3>
                  <p className="text-xs text-stone-500 mb-4">
                    Comparison between rolling 7-day velocity and current month totals
                  </p>

                  <div className="space-y-3.5">
                    {/* Last 7 Days Row */}
                    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-stone-800">
                          Last 7 Days
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-stone-200 text-stone-700">
                          {insightsData.last7Days?.totals?.count || 0} jobs
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-stone-400 block">Gross GMV</span>
                          <span className="font-bold text-stone-800">
                            ₹{(insightsData.last7Days?.totals?.gross || 0).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-stone-400 block">Commission</span>
                          <span className="font-bold text-emerald-700">
                            ₹{(insightsData.last7Days?.totals?.adminCommission || 0).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-stone-400 block">Provider Share</span>
                          <span className="font-bold text-stone-800">
                            ₹{(insightsData.last7Days?.totals?.providerEarnings || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Current Month Row */}
                    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-stone-800">
                          Current Month (MTD)
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-stone-200 text-stone-700">
                          {insightsData.currentMonth?.totals?.count || 0} jobs
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-stone-400 block">Gross GMV</span>
                          <span className="font-bold text-stone-800">
                            ₹{(insightsData.currentMonth?.totals?.gross || 0).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-stone-400 block">Commission</span>
                          <span className="font-bold text-emerald-700">
                            ₹{(insightsData.currentMonth?.totals?.adminCommission || 0).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-stone-400 block">Provider Share</span>
                          <span className="font-bold text-stone-800">
                            ₹{(insightsData.currentMonth?.totals?.providerEarnings || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {insightsData.peakDay && (
                  <div className="mt-4 pt-3 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500">
                    <span>Highest Revenue Day (Past Week):</span>
                    <span className="font-bold text-stone-900">
                      {insightsData.peakDay.date} (₹{insightsData.peakDay.adminCommission} comm.)
                    </span>
                  </div>
                )}
              </div>

              {/* Top Services Leaderboard */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-xs">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-stone-900">
                      Top Earning Trades & Services
                    </h3>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Leading categories ranked by platform commission
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-stone-400">
                    Past 7 Days
                  </span>
                </div>

                {insightsData.topServices && insightsData.topServices.length > 0 ? (
                  <div className="space-y-3">
                    {insightsData.topServices.slice(0, 4).map((service, idx) => (
                      <div
                        key={idx}
                        className="p-3.5 rounded-xl border border-stone-200/80 bg-stone-50/60 hover:bg-stone-50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-stone-200 text-stone-800 flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <h4 className="font-bold text-sm text-stone-900">
                              {service.serviceName}
                            </h4>
                          </div>
                          <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md text-xs font-bold">
                            {service.bookings} jobs
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-stone-400 block">Gross:</span>
                            <span className="font-bold text-stone-800">
                              ₹{(service.grossAmount || 0).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="text-stone-400 block">Commission:</span>
                            <span className="font-bold text-emerald-700">
                              +₹{(service.adminCommission || 0).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="text-stone-400 block">Buddy Share:</span>
                            <span className="font-bold text-stone-700">
                              ₹{(service.providerEarnings || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-stone-400 text-xs">
                    No trade analytics available for this window
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 4: Daily Trend or Transaction Ledger Tabs */}
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-xs">
            {/* Table Header & View Switcher */}
            <div className="p-5 lg:p-6 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-stone-50/50">
              <div>
                <h3 className="text-lg font-bold text-stone-900">
                  Financial Audit Ledger
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Detailed journal of every commission event, booking remittance, and provider payout.
                </p>
              </div>

              {/* View Switcher Tabs */}
              <div className="flex items-center gap-1.5 p-1 bg-stone-200/70 rounded-xl self-start sm:self-auto">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    activeTab === "all"
                      ? "bg-white text-stone-900 shadow-xs"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  Transaction Feed ({transactionsData?.pagination?.total || 0})
                </button>
                {earningsData?.daily && earningsData.daily.length > 0 && (
                  <button
                    onClick={() => setActiveTab("daily")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      activeTab === "daily"
                        ? "bg-white text-stone-900 shadow-xs"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    Daily Timeline ({earningsData.daily.length} Days)
                  </button>
                )}
              </div>
            </div>

            {/* Daily Timeline View */}
            {activeTab === "daily" && earningsData?.daily && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-50/80 text-stone-500 font-bold uppercase tracking-wider">
                      <th className="py-3.5 px-6">Date</th>
                      <th className="py-3.5 px-6 text-center">Completed Jobs</th>
                      <th className="py-3.5 px-6 text-right">Gross GMV</th>
                      <th className="py-3.5 px-6 text-right">Platform Commission</th>
                      <th className="py-3.5 px-6 text-right">Provider Net Share</th>
                      <th className="py-3.5 px-6 text-center">Effective Cut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 font-medium">
                    {earningsData.daily.map((day, idx) => (
                      <tr key={idx} className="hover:bg-stone-50/70 transition-colors">
                        <td className="py-3.5 px-6 font-bold text-stone-900">
                          {day.date}
                        </td>
                        <td className="py-3.5 px-6 text-center">
                          <span className="px-2.5 py-0.5 rounded-md bg-stone-100 text-stone-800 font-semibold">
                            {day.bookings}
                          </span>
                        </td>
                        <td className="py-3.5 px-6 text-right font-bold text-stone-800">
                          ₹{(day.grossAmount || 0).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-6 text-right font-bold text-emerald-700">
                          +₹{(day.adminCommission || 0).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-6 text-right font-bold text-stone-700">
                          ₹{(day.providerEarnings || 0).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-6 text-center">
                          <span className="text-[11px] font-semibold text-stone-500">
                            {day.grossAmount > 0
                              ? `${((day.adminCommission / day.grossAmount) * 100).toFixed(1)}%`
                              : "0%"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Transaction Feed View */}
            {activeTab === "all" && (
              <div className="overflow-x-auto">
                {transactionsData?.transactions &&
                transactionsData.transactions.length > 0 ? (
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-stone-200 bg-stone-50/80 text-stone-500 font-bold uppercase tracking-wider">
                        <th className="py-3.5 px-6">Event ID & Date</th>
                        <th className="py-3.5 px-6">Service / Trade</th>
                        <th className="py-3.5 px-6">Provider / Buddy</th>
                        <th className="py-3.5 px-6">Type & Reference</th>
                        <th className="py-3.5 px-6 text-right">Gross Order</th>
                        <th className="py-3.5 px-6 text-right">Commission Cut</th>
                        <th className="py-3.5 px-6 text-right">Buddy Payout</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 font-medium">
                      {transactionsData.transactions.map((txn) => {
                        const sName =
                          txn.booking?.service?.name ||
                          txn.service?.name ||
                          "General Service";
                        const subName =
                          txn.booking?.subService?.name ||
                          txn.subService?.name ||
                          "Direct Job";

                        return (
                          <tr
                            key={txn.id}
                            className="hover:bg-stone-50/80 transition-colors"
                          >
                            {/* ID & Date */}
                            <td className="py-4 px-6">
                              <div className="font-mono font-bold text-stone-900 text-xs">
                                {txn.id?.slice(-8) || "TXN"}
                              </div>
                              <div className="text-[11px] text-stone-400 mt-0.5">
                                {new Date(txn.createdAt).toLocaleDateString()} •{" "}
                                {new Date(txn.createdAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </td>

                            {/* Service / Trade */}
                            <td className="py-4 px-6">
                              <div className="font-bold text-stone-900 text-xs">
                                {sName}
                              </div>
                              <div className="text-[11px] text-stone-500 mt-0.5">
                                {subName}
                              </div>
                            </td>

                            {/* Provider */}
                            <td className="py-4 px-6">
                              {txn.provider ? (
                                <div>
                                  <div className="font-semibold text-stone-800">
                                    {txn.provider.name}
                                  </div>
                                  <div className="text-[11px] text-stone-400">
                                    {txn.provider.phone || "No Phone"}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-stone-400 italic">Platform</span>
                              )}
                            </td>

                            {/* Type & Booking ID */}
                            <td className="py-4 px-6">
                              <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-700 border border-stone-200">
                                {txn.type || "COMMISSION"}
                              </span>
                              {txn.booking?.id && (
                                <div className="text-[11px] text-stone-400 mt-1 font-mono">
                                  #{txn.booking.id.slice(-8)}
                                </div>
                              )}
                            </td>

                            {/* Gross Order */}
                            <td className="py-4 px-6 text-right font-bold text-stone-800">
                              ₹
                              {(
                                txn.booking?.grossAmount ||
                                txn.booking?.finalPrice ||
                                txn.booking?.resolvedPrice ||
                                txn.amount ||
                                0
                              ).toLocaleString()}
                            </td>

                            {/* Commission */}
                            <td className="py-4 px-6 text-right font-extrabold text-emerald-700 text-sm">
                              +₹{(txn.amount || txn.booking?.adminCommission || 0).toLocaleString()}
                            </td>

                            {/* Buddy Payout */}
                            <td className="py-4 px-6 text-right font-semibold text-stone-600">
                              ₹
                              {(
                                txn.booking?.providerEarnings || 0
                              ).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-12 h-12 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-3 text-stone-400">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h4 className="font-bold text-stone-800 text-sm">
                      No Financial Transactions Found
                    </h4>
                    <p className="text-xs text-stone-400 mt-1 max-w-sm mx-auto">
                      No commission or remittance entries match the selected filters and date range.
                    </p>
                  </div>
                )}

                {/* Pagination Controls */}
                {transactionsData?.pagination && transactionsData.pagination.total > 0 && (
                  <div className="p-4 lg:p-6 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-stone-50/50">
                    <p className="text-xs text-stone-500 font-medium">
                      Showing{" "}
                      <span className="font-bold text-stone-800">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-bold text-stone-800">
                        {Math.min(
                          currentPage * itemsPerPage,
                          transactionsData.pagination.total
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-bold text-stone-800">
                        {transactionsData.pagination.total}
                      </span>{" "}
                      transactions
                    </p>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-xl border border-stone-300 text-stone-700 text-xs font-semibold hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        ← Prev
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-8 h-8 rounded-xl text-xs font-bold transition-all ${
                                currentPage === pageNum
                                  ? "bg-[#1C1917] text-white shadow-xs"
                                  : "border border-stone-300 text-stone-700 hover:bg-stone-100"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() =>
                          handlePageChange(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-xl border border-stone-300 text-stone-700 text-xs font-semibold hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Finance;
